import React, { useState, useEffect, useCallback } from 'react';
import { paymentsAPI } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentsAPI.getAll({ page, limit: 10, status: statusFilter });
      setPayments(res.data.data);
      setPagination(res.data.pagination);
    } catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleMarkPaid = async (id) => {
    const transactionId = prompt('Transaction ID (optional):') || '';
    try { await paymentsAPI.markPaid(id, { transactionId }); toast.success('Payment marked as paid'); fetchPayments(); }
    catch { toast.error('Failed'); }
  };

  const handleReminder = async (id) => {
    try { await paymentsAPI.sendReminder(id); toast.success('Reminder sent!'); }
    catch { toast.error('Failed to send reminder'); }
  };

  const handleExport = async () => {
    try {
      const res = await paymentsAPI.exportCSV({ status: statusFilter });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'payment_report.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value="">All Status</option>
          {['pending','paid','failed','expired'].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
        <button onClick={handleExport} className="ml-auto px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
          Export CSV
        </button>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? <div className="p-12"><Spinner /></div> : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-6 py-3 font-medium">Invoice</th>
                  <th className="px-6 py-3 font-medium">Customer</th>
                  <th className="px-6 py-3 font-medium">Plan</th>
                  <th className="px-6 py-3 font-medium">Amount</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Due Date</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map(p => (
                  <tr key={p._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-mono text-xs">{p.invoiceNumber}</td>
                    <td className="px-6 py-4"><p className="font-medium">{p.customer?.name}</p><p className="text-xs text-gray-500">{p.customer?.email}</p></td>
                    <td className="px-6 py-4">{p.plan?.name}</td>
                    <td className="px-6 py-4 font-semibold">{formatCurrency(p.amount)}</td>
                    <td className="px-6 py-4"><Badge status={p.status} /></td>
                    <td className="px-6 py-4 text-gray-500">{formatDate(p.dueDate)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <PermissionGuard module="payments" action="update">
                          {['pending','failed'].includes(p.status) && <button onClick={() => handleMarkPaid(p._id)} className="text-green-600 hover:underline text-xs">Mark Paid</button>}
                          {p.status === 'pending' && <button onClick={() => handleReminder(p._id)} className="text-blue-600 hover:underline text-xs">Remind</button>}
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-400">No payments found</td></tr>}
              </tbody>
            </table>
            <Pagination pagination={pagination} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
};

export default Payments;
